/**
 * PortalLayout — Layout minimalista para el portal aislado del colaborador (Caso A).
 *
 * Header con logo + nombre del empleado + logout.
 * Sin sidebar del ERP — diseñado para empleados que no son usuarios del ERP.
 */
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { recordarDestino } from '@/lib/destinoTrasLogin'
import { LogOut } from 'lucide-react'
import { EnvBanner } from '@/components/EnvBanner'
import { PortalNav } from '../components/PortalNav'
import { CambiarContrasena } from '../components/CambiarContrasena'
import { usePortalAuthStore } from '@/store/portalAuthStore'
import { usePortalLogout } from '../hooks/useEmployeePortal'
import { usePortalMe } from '../hooks/useEmployeePortal'
import { Avatar } from '@/components/ds/Avatar'
import { InterruptorMontos } from '@/components/ds/InterruptorMontos'

function RequirePortalAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = usePortalAuthStore()
  const { pathname, search } = useLocation()

  if (!isAuthenticated) {
    // Para volver a lo que se estaba abriendo —p. ej. el enlace del aviso de
    // firma— después de entrar (#1656).
    recordarDestino(pathname + search)
    return <Navigate to="/colaboradores/login" replace />
  }

  return <>{children}</>
}

function PortalHeader() {
  const { employee } = usePortalAuthStore()
  // La foto sale de `me/` (URL firmada de vida corta), no del store de la sesión.
  const { data: me } = usePortalMe()
  const logoutMutation = usePortalLogout()
  const { logout } = usePortalAuthStore()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync()
    } finally {
      logout()
      navigate('/colaboradores/login', { replace: true })
    }
  }

  return (
    <header className="cl-topbar border-b border-divider px-6 py-3 flex items-center justify-between shrink-0">
      <div className="flex items-center gap-3">
        <img src="/clobi-logo-light-compacto.svg" alt="Clobi" className="h-7 dark:hidden" />
        <img src="/clobi-logo-white-compacto.svg" alt="Clobi" className="h-7 hidden dark:block" />
        <span className="text-sm font-medium text-content-muted hidden sm:inline">
          Portal del Colaborador
        </span>
      </div>
      <div className="flex items-center gap-4">
        {/* Montos ocultos por defecto; un solo interruptor para todo el portal (plan, D5). */}
        <InterruptorMontos />
        {employee && (
          <span className="flex items-center gap-2 text-sm text-content-muted">
            <Avatar nombre={employee.full_name} fotoUrl={me?.photo_url} tamano="sm" decorativa />
            <span className="hidden sm:inline">Hola, <strong className="text-content">{employee.full_name}</strong></span>
          </span>
        )}
        {/* Solo acá, en el Caso A: quien entra con su usuario del ERP no tiene
            contraseña del portal. */}
        <CambiarContrasena />
        <button
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
          className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content
                     transition-colors disabled:opacity-60"
          aria-label="Cerrar sesión del portal"
        >
          <LogOut size={15} aria-hidden="true" />
          <span className="hidden sm:inline">Salir</span>
        </button>
      </div>
    </header>
  )
}

export default function PortalLayout() {
  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <EnvBanner />
      <PortalHeader />
      <PortalNav />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}

export { RequirePortalAuth }
